import { create } from 'zustand';
import { subscriptionApi } from '@/lib/api';
import type { SubscriptionStatus, SubscriptionPlan, Transaction, CheckoutData } from '@/lib/api';

interface SubscriptionState {
  status: SubscriptionStatus | null;
  plans: SubscriptionPlan[];
  transactions: Transaction[];
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchStatus: () => Promise<void>;
  fetchPlans: () => Promise<void>;
  fetchTransactions: () => Promise<void>;
  createCheckout: (plan: string, billingPeriod?: 'monthly' | 'annual') => Promise<{ success: boolean; data?: CheckoutData; error?: string }>;
  cancelSubscription: () => Promise<{ success: boolean; error?: string }>;
  resumeSubscription: () => Promise<{ success: boolean; error?: string }>;
  changePlan: (plan: string, billingPeriod?: 'monthly' | 'annual') => Promise<{ success: boolean; error?: string }>;
  clearError: () => void;
}

export const useSubscriptionStore = create<SubscriptionState>((set, get) => ({
  status: null,
  plans: [],
  transactions: [],
  isLoading: false,
  error: null,

  clearError: () => set({ error: null }),

  fetchStatus: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await subscriptionApi.getStatus();
      set({ status: response.data, isLoading: false });
    } catch (error) {
      set({
        isLoading: false,
        error: error instanceof Error ? error.message : 'Error al cargar el estado de la suscripción'
      });
    }
  },

  fetchPlans: async () => {
    try {
      const response = await subscriptionApi.getPlans();
      set({ plans: response.data.plans });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Error al cargar los planes'
      });
    }
  },

  fetchTransactions: async () => {
    try {
      const response = await subscriptionApi.getTransactions();
      set({ transactions: response.data.transactions });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Error al cargar las transacciones'
      });
    }
  },

  createCheckout: async (plan, billingPeriod = 'monthly') => {
    set({ isLoading: true, error: null });
    try {
      const response = await subscriptionApi.createCheckout(plan, billingPeriod);
      set({ isLoading: false });
      return { success: true, data: response.data };
    } catch (error: unknown) {
      const errorMessage = (error as { response?: { data?: { message?: string } } })?.response?.data?.message
        || (error instanceof Error ? error.message : 'Error al crear el checkout');
      set({ isLoading: false, error: errorMessage });
      return { success: false, error: errorMessage };
    }
  },

  cancelSubscription: async () => {
    set({ isLoading: true, error: null });
    try {
      await subscriptionApi.cancel();
      await get().fetchStatus();
      set({ isLoading: false });
      return { success: true };
    } catch (error: unknown) {
      const errorMessage = (error as { response?: { data?: { message?: string } } })?.response?.data?.message
        || (error instanceof Error ? error.message : 'Error al cancelar la suscripción');
      set({ isLoading: false, error: errorMessage });
      return { success: false, error: errorMessage };
    }
  },

  resumeSubscription: async () => {
    set({ isLoading: true, error: null });
    try {
      await subscriptionApi.resume();
      await get().fetchStatus();
      set({ isLoading: false });
      return { success: true };
    } catch (error: unknown) {
      const errorMessage = (error as { response?: { data?: { message?: string } } })?.response?.data?.message
        || (error instanceof Error ? error.message : 'Error al reanudar la suscripción');
      set({ isLoading: false, error: errorMessage });
      return { success: false, error: errorMessage };
    }
  },

  changePlan: async (plan, billingPeriod = 'monthly') => {
    set({ isLoading: true, error: null });
    try {
      await subscriptionApi.changePlan(plan, billingPeriod);
      await get().fetchStatus();
      set({ isLoading: false });
      return { success: true };
    } catch (error: unknown) {
      const errorMessage = (error as { response?: { data?: { message?: string } } })?.response?.data?.message
        || (error instanceof Error ? error.message : 'Error al cambiar el plan');
      set({ isLoading: false, error: errorMessage });
      return { success: false, error: errorMessage };
    }
  },
}));
